<template>
  <v-row
    v-if="summary"
    :dense="$vuetify.display.name === 'xs'"
    class="mt-0"
    style="line-height: normal"
    no-gutters
  >
    <v-col
      cols="12"
      sm="6"
    >
      <div class="fs-16 fw-800 mb-1">
        {{ $bct('Relationship') }}
      </div>
      <div
        v-if="householdMember.relationship"
        class="fs-16"
      >
        {{ $bct(householdMember.relationship) }}
      </div>
    </v-col>
    <v-col
      cols="12"
      sm="6"
    >
      <div class="fs-16 fw-800 mb-1">
        {{ $bct('Date of birth') }}
      </div>
      <div class="fs-16">
        <LongDate
          v-if="householdMember.dob"
          :date="householdMember.dob"
          class="fs-16"
          timezone="UTC"
        />
        <span
          v-else
          class="text-grey"
        >
          &#x2014;
        </span>
      </div>
    </v-col>
  </v-row>

  <v-row
    v-else
    :dense="$vuetify.display.name === 'xs'"
    class="mt-0"
    style="line-height: normal"
  >
    <v-col
      cols="12"
      sm="3"
    >
      <div class="mb-6">
        <div class="fs-16 fw-600">
          {{ $bct('Relationship') }}
        </div>
        <div
          v-if="householdMember.relationship"
          class="fs-16"
        >
          {{ $bct(householdMember.relationship) }}
        </div>
      </div>

      <div>
        <div class="fs-16 fw-600">
          {{ $bct('Race') }}
        </div>

        <div class="fs-16">
          {{ race }}
          <span
            v-if="!race"
            class="text-grey"
          >
            &#x2014;
          </span>
        </div>
      </div>
    </v-col>

    <v-col
      class="flex align-center"
      cols="12"
      sm="3"
    >
      <v-row>
        <v-col>
          <v-divider vertical />
        </v-col>
        <v-col cols="10">
          <div
            v-if="!hideSsn"
            class="mb-6"
          >
            <div class="fs-16 fw-600">
              {{ $bct('SSN') }}
            </div>

            <div class="fs-16">
              {{ maskSSN(householdMember.ssn) }}
              <span
                v-if="!householdMember.ssn"
                class="text-grey"
              >
                &#x2014;
              </span>
              <v-icon
                @click="toggleSSN"
                @keyup.enter="toggleSSN"
                :icon="showSSN ? 'visibility_off' : 'visibility'"
              />
            </div>
          </div>

          <div>
            <div class="fs-16 fw-600">
              {{ $bct('Ethnicity') }}
            </div>

            <div
              v-if="householdMember.ethnicity"
              class="fs-16"
            >
              {{ $bct(householdMember.ethnicity) }}
            </div>
          </div>
        </v-col>
        <v-col>
          <v-divider vertical />
        </v-col>
      </v-row>
    </v-col>

    <v-col
      cols="12"
      sm="3"
    >
      <div class="mb-6">
        <div class="fs-16 fw-600">
          {{ $bct('Sex') }}
        </div>
        <div
          v-if="householdMember.gender"
          class="fs-16"
        >
          {{ $bct(householdMember.gender) }}
        </div>
      </div>

      <div>
        <div class="fs-16 fw-600">
          {{ $bct('Date of birth') }}
        </div>

        <div class="fs-16">
          <LongDate
            :date="householdMember.dob"
            class="fs-16"
            timezone="UTC"
          />
        </div>
      </div>
    </v-col>

    <v-col
      class="d-flex justify-end"
      cols="12"
      sm="3"
    >
      <v-btn
        v-if="canEdit"
        @click="edit"
        :ripple="false"
        class="focus-very-visible me-3"
        color="primary"
        data-testId="edit-household-member-button"
        size="large"
        variant="outlined"
      >
        {{ $bct('Edit') }}
      </v-btn>
    </v-col>
  </v-row>
</template>

<script setup>
import _ from 'lodash';

import LongDate from '@/shared/components/LongDate.vue';

const emit = defineEmits(['edit']);
const props = defineProps({
  hideSsn: {
    type: Boolean,
    default: false,
  },
  householdMember: {
    type: Object,
    required: true,
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
  summary: {
    type: Boolean,
    default: false,
  },
});

const $bct = inject('translator');

const showSSN = ref(false);

const race = computed(() => {
  return props.householdMember.race?.length > 0
    ? props.householdMember.race.map((race) => $bct(race)).join(', ')
    : null;
});

function edit() {
  emit('edit', props.householdMember.value);
}

function maskSSN(ssn) {
  if (_.isEmpty(ssn)) return null;

  return showSSN.value ? ssn : `XXX-XX${ssn.substring(6)}`;
}

function toggleSSN() {
  showSSN.value = !showSSN.value;
}
</script>
